import cx from 'classnames';
import { type FunctionComponent, type ReactNode, useMemo, type VoidFunctionComponent } from 'react';

import Button from '../../generic/Button/Button';
import ButtonGroup from '../../generic/Button/ButtonGroup';
import LoaderButton from '../../generic/Button/LoaderButton';
import NavigationButton from '../../generic/Button/NavigationButton';
import TooltipButton from '../../generic/Button/TooltipButton';
import { ErrorAlert } from '../../generic/ErrorAlert';
import { icons, IconText } from '../../generic/Icon';
import RouterLink from '../../generic/Link/RouterLink';
import { defineSlotComponent, findAllForSlot, findFirstForSlot } from '../../layouts/slots';
import type { ButtonProps } from '../../generic/Button/Button';
import type { LoaderButtonProps } from '../../generic/Button/LoaderButton';
import type { NavigationButtonProps } from '../../generic/Button/NavigationButton';
import type { TooltipButtonProps } from '../../generic/Button/TooltipButton';

import Paragraph from './SettingsPageParagraph';

import styles from './Settings.module.less';

type Props = {
  /**
   * Text for the page heading.
   */
  heading: string | ReactNode;
  /**
   * Optional padding. Helpful for old pages which
   * have full-width tables but need padding on the Header.
   */
  isPadded?: boolean;
};

type SettingsPageHeaderComponent = FunctionComponent<Props> & {
  Button: FunctionComponent<Pick<ButtonProps, 'type' | 'size' | 'onClick' | 'isDisabled'>>;
  SaveButton: FunctionComponent<Pick<LoaderButtonProps, 'onClick' | 'isDisabled' | 'isLoading'>>;
  TooltipButton: FunctionComponent<Pick<TooltipButtonProps, 'id' | 'onClick' | 'isDisabled' | 'showTooltip' | 'tooltip'>>;
  NavigationButton: FunctionComponent<Pick<NavigationButtonProps, 'to' | 'type' | 'size'>>;
  Paragraph: FunctionComponent;
  Errors: VoidFunctionComponent<{ errors: string[] | null }>;
  Warning: VoidFunctionComponent<{ message: string }>;
  BackLink: VoidFunctionComponent<{ to: string; text: string }>;
};

/**
 * SettingsPageHeader is a component for the page header,
 * which always includes the page heading and will also sometimes
 * include a SaveButton, which is provided as a slot child component.
 *
 * @example
 * <SettingsPageHeader heading="Default general settings">
 *  <SettingsPageHeader.SaveButton onClick={() => console.log('save!')} />
 * </SettingsPageHeader>
 */
const SettingsPageHeader: SettingsPageHeaderComponent = ({ heading, isPadded, children }) => {
  const buttons = findAllForSlot('Button', children);
  const paragraphs = findAllForSlot('Paragraph', children);
  const errors = findFirstForSlot('Errors', children);
  const warning = findFirstForSlot('Warning', children);
  const backLink = findFirstForSlot('BackLink', children);
  return (
    <header className={cx(styles.header, {
      [styles.headerPadded]: isPadded,
    })}>
      {backLink && (
        <div className={styles.backLink}>
          {backLink}
        </div>
      )}
      <div className={styles.headerRow}>
        <h3 className={styles.heading}>
          {heading}
        </h3>
        {/* If there is only a single button, just render it. React can render arrays like this now. */}
        {buttons.length === 1 && buttons}
        {buttons.length > 1 && (
          <ButtonGroup>
            {buttons}
          </ButtonGroup>
        )}
      </div>
      {paragraphs}
      {(errors || warning) && (
        <div className={styles.alerts}>
          {errors}
          {warning}
        </div>
      )}
    </header>
  );
};

/**
 * Generic Button. Treated internally as the same as a child of Header.
 * Provide multiple Button components and they will be rendered in order.
 * This makes putting a button beside the SaveButton possible.
 */
SettingsPageHeader.Button = defineSlotComponent('Button', ({ children, type = 'secondary', size = 'medium', onClick, isDisabled }) => (
  <Button
    type={type}
    size={size}
    onClick={onClick}
    isDisabled={isDisabled}
  >
    {children}
  </Button>
));

/**
 * This is tagged as a Button child, but is more specifically a SaveButton.
 */
SettingsPageHeader.SaveButton = defineSlotComponent('Button', ({ isDisabled, onClick, isLoading }) => (
  <LoaderButton
    size="medium"
    type="primary"
    onClick={onClick}
    isDisabled={isDisabled}
    isLoading={isLoading}
  >
    Save
  </LoaderButton>
));

/**
 * TooltipButton
 */
SettingsPageHeader.TooltipButton = defineSlotComponent('Button', ({ children, id, onClick, isDisabled, showTooltip, tooltip }) => (
  <TooltipButton
    id={id}
    type="secondary"
    size="medium"
    onClick={onClick}
    isDisabled={isDisabled}
    showTooltip={showTooltip}
    tooltip={tooltip}
  >
    {children}
  </TooltipButton>
));

/**
 * Navigation button, which can replace some of the docs-style buttons we use.
 */
SettingsPageHeader.NavigationButton = defineSlotComponent('Button', ({ children, to, type = 'secondary', size = 'medium' }) => (
  <NavigationButton
    to={to}
    type={type}
    size={size}
    openInNewTab
  >
    {children}
  </NavigationButton>
));

SettingsPageHeader.Paragraph = defineSlotComponent('Paragraph', ({ children }) => (
  <Paragraph size="medium">
    {children}
  </Paragraph>
));

SettingsPageHeader.Errors = defineSlotComponent('Errors', ({ errors }) => (
  <ErrorAlert status={useMemo(() => ({ errors }), [errors])} />
));

SettingsPageHeader.Warning = defineSlotComponent('Warning', ({ message }) => (
  <div className="alert alert-warning width-full" role="alert">
    {message}
  </div>
));

SettingsPageHeader.BackLink = defineSlotComponent('BackLink', ({ to, text }) => (
  <RouterLink to={to}>
    <IconText
      icon={icons.angleLeft}
      text={text}
    />
  </RouterLink>
));

export default SettingsPageHeader;
